import {SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {useForm, Controller} from 'react-hook-form';
import {useQuery, QueryFunctionContext} from 'react-query';

import Typography from 'components/Typography';
import PageHeader from 'components/PageHeader';
import Button from 'components/Button';
import FormActionGroup from 'components/FormActionGroup';
import FormGroup from 'components/FormGroup';
import TextField from 'components/TextField';
import SelectField from 'components/Select';
import {components} from 'react-select';
import api from 'api';

import {Driver, ListResponse, User, Vehicle} from 'types';
import {get} from 'lodash';
import Modal from 'components/Modal';
import EmployeeForm from 'pages/EmployeeForm';
import PlusComponent from 'components/PlusComponent';
import Loader from 'components/DataTable/components/Loader';
import {HexColorPicker} from 'react-colorful';
import './colorful.css';
import styles from './styles.module.scss';
import classNames from 'classnames';
import Tick from './tick';

interface Option {
  label: string;
  value: number;
}

interface FormValue {
  name: string;
  model: string;
  email: string;
  license_plate_number: string;
  driver?: Option[];
  travel_method?: {
    value: 'car' | 'bike';
    label: string;
  };
}

export async function getDrivers() {
  const {data} = await api.get<ListResponse<Driver>>('/drivers/');

  return data;
}

async function getVehicleById({queryKey}: QueryFunctionContext) {
  const [, vehicleId] = queryKey;

  if (!vehicleId) return undefined;

  const {data} = await api.get<Vehicle>(`/teams/${vehicleId}`);

  return data;
}

const colors = [
  '#4000FF',
  '#FF5733', // bright orange
  '#33C1FF', // sky blue
  '#9B59B6', // purple
  '#2ECC71', // emerald green
  '#F1C40F', // yellow
  '#E67E22', // carrot orange
  '#1ABC9C', // turquoise
  // '#E74C3C', // red
  // '#2980B9', // strong blue
  // '#8E44AD', // deep purple
  // '#16A085', // teal
  // '#F39C12', // golden orange
  // '#D35400', // pumpkin
  // '#7F8C8D', // grayish
  // '#BDC3C7', // silver-ish
  // '#C0392B', // dark red
  // '#27AE60', // fresh green
  // '#34495E', // navy blue
  // '#3498DB', // light blue
  // '#A569BD', // light purple
];

type Props = {
  isModal?: boolean;
  setUser?: (data: any) => void;
  setModalVisible?: (data: SetStateAction<boolean>) => void;
};

function VehicleForm({
  isModal,
  setUser: setUserModal,
  setModalVisible: setHideMemberModal,
}: Props) {
  const {vehicleId} = useParams<{vehicleId?: string}>();
  const {push, replace} = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [color, setColor] = useState(colors[0]);

  const {
    handleSubmit,
    register,
    control,
    formState: {errors},
    reset,
    setValue,
  } = useForm<FormValue>();

  const {data: drivers, refetch} = useQuery('drivers', getDrivers);
  const {data: vehicle, isLoading} = useQuery(
    ['vehicle', vehicleId],
    getVehicleById
  );

  const options = useMemo(
    function () {
      if (!drivers) return [];

      return (
        drivers.results
          // .filter((driver) => !driver.is_assigned)
          .map((d) => ({
            value: d.id,
            label: d.full_name,
            isActive: d.is_active === 'True',
          }))
      );
    },
    [drivers]
  );

  useEffect(
    function () {
      if (!vehicle) return;

      reset({
        ...vehicle,
        driver: vehicle.drivers
          ? vehicle.drivers.map((item) => ({
              value: item.id,
              label: item.first_name + ' ' + item.last_name,
            }))
          : undefined,
        travel_method: vehicle.travel_method
          ? {
              value: vehicle.travel_method,
              label: vehicle.travel_method === 'car' ? 'Car' : 'Bike',
            }
          : {
              value: 'car',
              label: 'Car',
            },
      });
    },
    [vehicle, reset]
  );

  async function submit(formValue: FormValue) {
    setSubmitting(true);

    const data = {
      color,
      model: formValue.model,
      name: formValue.name,
      drivers: formValue.driver?.map((item) => item.value),
      travel_method: get(formValue, 'travel_method.value', 'car'),
    };

    try {
      if (vehicleId) {
        await api.put(`/teams/${vehicleId}/`, data);
      } else {
        const team = await api.post('/teams/', data);
        if (setUserModal) setUserModal(team.data);
      }
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }

    if (!isModal) push('/people/teams');
  }

  const handleDeleteEmployee = useCallback(
    async (id: number) => {
      try {
        await api.delete(`/teams/${id}/`);
        replace('/people/teams');
      } catch (error) {}
    },
    [replace]
  );

  const setUser = (data: User) => {
    setModalVisible(false);
    refetch();
    setValue('driver', [
      {
        label: data.first_name + ' ' + data.last_name,
        value: data.id,
      },
    ]);
  };

  return (
    <div style={{position: 'relative'}}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            right: 0,
            left: 0,
            height: '100%',
            padding: 0,
            margin: 0,
            zIndex: 1,
          }}
        >
          <Loader />
        </div>
      )}
      <PageHeader>
        <Typography variant="h2">
          {vehicleId ? 'Edit Team' : 'New Team'}
        </Typography>
        {!isModal && (
          <FormActionGroup>
            {!!vehicleId && (
              <Button
                onClick={() => handleDeleteEmployee(+vehicleId)}
                color="danger"
              >
                Delete
              </Button>
            )}
            <Button onClick={handleSubmit(submit)} loading={submitting}>
              Save
            </Button>
          </FormActionGroup>
        )}
      </PageHeader>
      <FormGroup columns={2}>
        <TextField
          label="Team name"
          placeholder="Name"
          {...register('name', {required: 'Required'})}
          error={errors.name?.message}
        />

        <TextField
          label="Vehicle name"
          placeholder="Vehicle name"
          {...register('model')}
          error={errors.model?.message}
        />
      </FormGroup>
      {!isModal && (
        <FormGroup columns={1}>
          <Controller
            name="driver"
            control={control}
            render={({field, fieldState}) => (
              <div style={{zIndex: 6}}>
                <div
                  style={{
                    marginBottom: 6,
                    marginTop: 22,
                  }}
                >
                  {!!isModal ? (
                    'Onsite Workers'
                  ) : (
                    <div
                      style={{display: 'flex', alignItems: 'center', gap: 10}}
                    >
                      <div>Onsite Workers</div>
                      <PlusComponent
                        onClick={() => {
                          if (!isModal) setModalVisible(true);
                        }}
                      />
                    </div>
                  )}
                </div>

                <SelectField
                  placeholder="Select Onsite Workers"
                  options={options}
                  components={{
                    Option: (props) => {
                      const {children, data} = props;

                      const isActive = options.find(
                        (item) => item.value === data.value
                      )?.isActive;

                      return (
                        <components.Option {...props}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 5,
                                marginRight: 5,
                                backgroundColor: isActive ? 'green' : 'red',
                              }}
                            />
                            {children}
                          </div>
                        </components.Option>
                      );
                    },
                    MultiValueContainer: (props) => {
                      const {children, data} = props;

                      const isActive = options.find(
                        (item) => item.value === data.value
                      )?.isActive;

                      return (
                        <div
                          style={{
                            display: 'flex',
                            marginRight: 5,
                            backgroundColor: '#e5e5e5',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 5,
                              marginLeft: 5,
                              backgroundColor: isActive ? 'green' : 'red',
                            }}
                          />
                          {children}
                        </div>
                      );
                    },
                  }}
                  //@ts-ignore
                  isMulti
                  error={fieldState.error?.message}
                  {...field}
                />
              </div>
            )}
          />
        </FormGroup>
      )}
      <FormGroup columns={1}>
        <Controller
          name="travel_method"
          control={control}
          render={({field, fieldState}) => (
            <div style={{zIndex: 5}}>
              <div
                style={{
                  marginBottom: 6,
                  cursor: 'pointer',
                  marginTop: 22,
                }}
              >
                Travel method
              </div>
              <SelectField
                placeholder="Select travel method"
                options={[
                  {value: 'car', label: 'Car'},
                  {value: 'bike', label: 'Bike'},
                ]}
                error={fieldState.error?.message}
                {...field}
              />
            </div>
          )}
        />
      </FormGroup>
      <p style={{marginTop: 20, fontSize: 17, marginBottom: 4}}>Team color:</p>

      <div className="colorful-component">
        <HexColorPicker color={color} onChange={setColor} />
      </div>
      <div className={styles.colors}>
        {colors.map((item) => {
          return (
            <div
              key={item}
              style={{
                backgroundColor: item,
              }}
              onClick={() => setColor(item)}
              className={classNames(
                styles.color,
                color === item && styles.selectedColor
              )}
            >
              {item === color && <Tick />}
            </div>
          );
        })}
      </div>
      {!!isModal && (
        <FormActionGroup style={{justifyContent: 'flex-end', marginTop: 20}}>
          {!!vehicleId && (
            <Button
              onClick={() => handleDeleteEmployee(+vehicleId)}
              color="danger"
            >
              Delete
            </Button>
          )}
          <Button onClick={handleSubmit(submit)} loading={submitting}>
            Save
          </Button>
        </FormActionGroup>
      )}
      <Modal
        isOpen={modalVisible}
        style={{
          content: {
            maxHeight: '90vh',
            overflow: 'scroll',
            minWidth: 800,
          },
        }}
        onClose={() => setModalVisible(false)}
        onRequestClose={() => setModalVisible(false)}
      >
        <EmployeeForm
          setModalVisible={setModalVisible}
          setUser={setUser}
          isModal={true}
        />
      </Modal>
    </div>
  );
}

export default VehicleForm;
